import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Button,
  Text,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { COUNTRY_CODES_AVAILABLE, CountryCode, countryKeyFromCode } from '../../constants/Countries.ts';
import AleaSearchIcon from '../../icons/AleaSearchIcon.tsx';
import { getFlagUrl } from '../../utils/flagUrl';

interface AleaCountryProps {
  label: string;
  value: CountryCode | '';
  onChange: (code: CountryCode) => void;
  error?: string;
  isDisabled?: boolean;
  placeholder?: string;
  countryOptions?: {
    value: CountryCode;
    label: string;
  }[];
}

const AleaCountry: React.FC<AleaCountryProps> = ({
  label,
  value,
  onChange,
  error,
  isDisabled = false,
  placeholder = '-',
  countryOptions,
}) => {
  const { t } = useTranslation();
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const BUTTON_WIDTH = 50;

  const options = useMemo(() => {
    if (countryOptions && countryOptions.length > 0) {
      return countryOptions.map((option) => ({
        value: option.value,
        name: option.label,
        label: option.label,
      }));
    }

    return COUNTRY_CODES_AVAILABLE.map((code) => ({
      value: code,
      name: t(countryKeyFromCode(code)),
      label: t(countryKeyFromCode(code)),
    }));
  }, [countryOptions, t]);

  const [selectedCountry, setSelectedCountry] = useState(() => {
    const found = options.find((o) => o.value === value);
    return found || options[0] || null;
  });

  useEffect(() => {
    const found = options.find((o) => o.value === value);
    if (found) {
      setSelectedCountry(found);
      return;
    }
    setSelectedCountry(options[0] || null);
  }, [value, options]);

  useEffect(() => {
    if (isCountryMenuOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
      if (containerRef.current) setMenuWidth(containerRef.current.offsetWidth);
    }
  }, [isCountryMenuOpen]);

  const filteredCountryOptions = options
    .filter((option) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return (
        option.name.toLowerCase().includes(q) ||
        option.value.toLowerCase().includes(q) ||
        option.label.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const formLabelStyles = {
    fontWeight: 300,
    marginBottom: '3px',
    color: 'alea.500',
    paddingLeft: '16px',
  };

  const menuButtonStyles = {
    borderLeftRadius: 0,
    border: '0',
    minW: `${BUTTON_WIDTH}px`,
    width: `${BUTTON_WIDTH}px`,
    height: '50px',
    bg: 'alea.500',
    color: 'white',
    padding: 0,
    borderRightRadius: '20px',
    _focus: { background: 'alea.500' },
    _focusVisible: { background: 'alea.500' },
    _active: { background: 'alea.500' },
    opacity: isDisabled ? 0.6 : 1,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;

  const menuListStyles = {
    height: '260px',
    overflowY: 'auto',
    width: '100%',
    minW: '100%',
    marginLeft: '0',
    paddingTop: '0',
    paddingBottom: '0',
    top: '0',
    borderRadius: '0',
    marginTop: '-1px',
  } as const;

  const displayBoxStyles = {
    flex: 1,
    alignItems: 'center',
    border: '1px solid',
    borderColor: isCountryMenuOpen ? 'white' : 'gray.300',
    borderRight: isCountryMenuOpen ? 'none' : '1px solid #DCDCDC',
    borderLeftRadius: '20px',
    borderRightRadius: '0px',
    bg: 'white',
    height: '50px',
    top: '0px',
    opacity: isDisabled ? 0.6 : 1,
  } as const;

  return (
    <FormControl isInvalid={!!error} isDisabled={isDisabled} width="100%">
      <FormLabel sx={formLabelStyles}>{label}</FormLabel>
      <Flex ref={containerRef} alignItems="center">
        <Flex sx={displayBoxStyles} position="relative" mr="0">
          {isCountryMenuOpen && (
            <Input
              ref={searchInputRef}
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              position="absolute"
              top={"-1px"}
              left={0}
              width="100%"
              height="50px"
              bg="white"
              border="1px solid"
              borderColor={isCountryMenuOpen ? 'gray.300' : 'white'}
              borderRadius="20px"
              borderTopRightRadius="0"
              borderBottomRightRadius="0"
              borderBottomLeftRadius="0"
              zIndex={1}
              focusBorderColor="none"
              _focus={{ boxShadow: 'none', borderColor: 'gray.300' }}
              pr="7px"
              pl="47px"
            />
          )}
          {isCountryMenuOpen && (
            <Box
              position="absolute"
              left="15px"
              top="50%"
              transform="translateY(-50%)"
              width="25px"
              height="25px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={2}
              pointerEvents="none"
            >
              <AleaSearchIcon size="25" />
            </Box>
          )}
          <Flex alignItems="center" pl="0">
            {selectedCountry && (
              <Box pl="10px" pr="20px">
                <Image
                  src={getFlagUrl(selectedCountry.value, { format: '4x3' })}
                  alt={selectedCountry.value}
                  height="28px"
                  width="auto"
                  borderRadius="6px"
                />
              </Box>
            )}
            <Text color={selectedCountry ? '#000' : 'gray.500'} fontSize="18px">
              {selectedCountry ? selectedCountry.name : placeholder}
            </Text>
          </Flex>
        </Flex>

        <Menu
          isOpen={isCountryMenuOpen}
          onOpen={() => {
            if (isDisabled) return;
            setIsCountryMenuOpen(true);
            setSearchQuery('');
            setTimeout(() => searchInputRef.current?.focus(), 0);
          }}
          onClose={() => setIsCountryMenuOpen(false)}
          placement="bottom-end"
          closeOnBlur={false}
          gutter={0}
          autoSelect={false}
          strategy="fixed"
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          <MenuButton as={Button} {...menuButtonStyles} sx={isCountryMenuOpen ? { borderBottomRightRadius: 0 } : undefined}>
            <Box as={ChevronDownIcon} color="white" boxSize="24px" />
          </MenuButton>
          <MenuList sx={{
            ...menuListStyles,
            width: menuWidth ? `${menuWidth}px` : menuListStyles.width,
            minW: menuWidth ? `${menuWidth}px` : menuListStyles.minW,
            marginLeft: '0'
          }}>
            {filteredCountryOptions.length === 0 && (
              <Flex alignItems="center" justifyContent="center" height="100%" color="#666" fontSize="14px">
                {t('general.noResults')}
              </Flex>
            )}
            {filteredCountryOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => {
                  setSelectedCountry(option);
                  onChange(option.value as CountryCode);
                  setIsCountryMenuOpen(false);
                }}
                px="0"
                sx={{ height: '50px', borderBottom: '1px solid #DCDCDC' }}
              >
                <Flex alignItems="center">
                  <Box ml="15px" mr="10px">
                    <Image src={getFlagUrl(option.value, { format: '4x3' })} alt={option.value} height="28px" />
                  </Box>
                  <Text color="#000" fontSize="16px" fontWeight={400}>
                    {option.name}
                  </Text>
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default AleaCountry;


